import React from 'react';
import { Route, Routes } from "react-router-dom";

import { canAccess } from '../models/AccessControl';
import { appRouteConfigs, canAccessDepartment } from './AppRouteConfig';
import { userStore } from "../stores/UserStore";
import { getUserRoleNames } from '../models/User';
import { departmentStore } from '../stores/DepartmentStore';
import { observer } from 'mobx-react-lite';
import Home from "../components/Home/Home";
import Logout from '../components/Auth/LogoutPage';

// Group 1: Admin only pages
const Users = React.lazy(() => import("../components/AdminBundle").then(m => ({ default: m.Users })));
const Departments = React.lazy(() => import("../components/AdminBundle").then(m => ({ default: m.Departments })));
const DepartmentPaymentTypeWorkflows = React.lazy(() => import("../components/AdminBundle").then(m => ({ default: m.DepartmentPaymentTypeWorkflows })));

// Group 2: Invoice-related pages (most used - bundle together)
const Invoices = React.lazy(() => import("../components/InvoiceBundle").then(m => ({ default: m.Invoices })));
const InvoiceImport = React.lazy(() => import("../components/InvoiceBundle").then(m => ({ default: m.InvoiceImport })));
const ProjectInvoices = React.lazy(() => import("../components/InvoiceBundle").then(m => ({ default: m.ProjectInvoices })));
const ProjectPayments = React.lazy(() => import("../components/InvoiceBundle").then(m => ({ default: m.ProjectPayments })));
const ExportLogs = React.lazy(() => import("../components/InvoiceBundle").then(m => ({ default: m.ExportLogs })));

// Group 3: Hourly Charges from Jira (least used - bundle together)
const HourlyCharges = React.lazy(() => import("../components/OtherBundle").then(m => ({ default: m.HourlyCharges })));

// Group 4: Reference Data (admin CRUD pages - bundle together)
const Accounts = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Accounts })));
const Categories = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Categories })));
const Customers = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Customers })));
const Locations = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Locations })));
const Splits = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Splits })));
const Vendors = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Vendors })));
const PaymentTypes = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.PaymentTypes })));
const Paytypes = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Paytypes })));
const Epictypes = React.lazy(() => import("../components/ReferenceDataBundle").then(m => ({ default: m.Epictypes })));


const RouteLoader = () => (
	<div className='center-content'>Loading...</div>
);

function AllRoutes() {
	const roles = getUserRoleNames(userStore.currentUser);
	const activeDepartment = departmentStore.activeDepartment;
	const canUseApplication = Boolean(userStore.currentUser?.isActive && activeDepartment);
	const isAccessLoading = userStore.isLoading || departmentStore.isLoading;

	const routeComponents: Record<string, React.ElementType> = {
		'/': Home,
		'/accounts': Accounts,
		'/categories': Categories,
		'/customers': Customers,
		'/locations': Locations,
		'/splits': Splits,
		'/admin/users': Users,
		'/admin/departments': Departments,
		'/vendors': Vendors,
		'/paymenttypes': PaymentTypes,
		'/paytypes': Paytypes,
		'/epictypes': Epictypes,
		'/admin/department-payment-type-workflows': DepartmentPaymentTypeWorkflows,
		'/hourly/view': HourlyCharges,
		'/invoices/view': Invoices,
		'/invoices/view/:invoiceId': Invoices,
		'/invoices/invoiceimport': InvoiceImport,
		'/invoices/projectinvoices': ProjectInvoices,
		'/invoices/projectpayments': ProjectPayments,
		'/invoices/exportlogs': ExportLogs,
		'/logout': Logout,
	};

	const routes = appRouteConfigs
		.map(route => ({
			...route,
			Component: routeComponents[route.path],
		}))
		.filter((route): route is typeof route & { Component: React.ElementType } => Boolean(route.Component));

	const homeRoute = routes.find(route => route.path === '/');
	const otherRoutes = routes.filter(route => route.path !== '/');

	if (!homeRoute) {
		return null;
	}

	return (
		<React.Suspense fallback={<RouteLoader />}>
			<Routes>
				<Route path={homeRoute.path} element={<homeRoute.Component />} />
				{otherRoutes
					.filter(route => canAccess(roles, route.roles) && canUseApplication && canAccessDepartment(route, activeDepartment))
					.map((route, index) => {
						const { path, Component } = route;
						return (
							<Route key={index} path={path} element={<Component />} />
						);
					})}
				<Route path='*' element={isAccessLoading ? <RouteLoader /> : <Home />} />
			</Routes>
		</React.Suspense>
	);
}

export default observer(AllRoutes);
